import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface OptionCardProps extends Omit<ComponentProps<'button'>, 'type'> {
  selected?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * How this product asks you to choose.
 *
 * It ships no checkbox at all: a choice is a bordered row that takes the brand
 * border and the brand tint when it is the one selected, with its glyph going
 * from `ink-dim` to the deeper coral. Lay them out in a `Grid` for the
 * several-across arrangement the product uses, or stack them for a list.
 *
 * Presentational on purpose. For a real radio group, hand it to Base UI's
 * `Radio.Root` through `render` so the roving focus and the form value come
 * with it.
 */
export function OptionCard({ selected, icon, className, children, ...props }: OptionCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-sm border px-3.5 py-3',
        'text-left text-bodysm font-medium text-ink',
        'transition-[background-color,border-color] duration-150 ease-out-quint',
        selected
          ? 'border-brand-400 bg-brand-200'
          : 'border-border-subtle bg-transparent hover:border-brand-400',
        'disabled:cursor-not-allowed disabled:border-border-subtle disabled:bg-transparent disabled:text-ink-faint',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          className={cn(
            'flex size-[18px] shrink-0 items-center',
            selected ? 'text-brand-600' : 'text-ink-dim',
          )}
        >
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
}
